import { Label, Separator } from 'react-aria-components';
import { Link } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';
import { startOfDay } from 'date-fns/startOfDay';

import ContactDetails from '@/components/ContactDetails';
import { StatusDueDate } from '@/components/DueDate';
import Accordion from '@/design_system/Accordion';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconCalendar from '@/icons/Calendar.svg';
import IconEdit from '@/icons/Edit.svg';
import IconFile from '@/icons/File.svg';
import IconPlace from '@/icons/Place.svg';
import {
  canBeCreatedBy,
  getArrivalDate,
  getDestination,
  getOrigin,
  ShipmentWithRelations,
  useCarrierName,
} from '@/models/shipment';
import { ShipmentOverview } from '@/routes/Shipments/Shipment/components/ShipmentOverview';
import { TrackingIdModal } from '@/routes/Shipments/Shipment/components/TrackingIdModal/TrackingIdModal';
import { useCurrentSession } from '@/services/auth';
import { formatDate } from '@/utils/date';
import { formatWeight } from '@/utils/number';
import { sumWeights } from '@/utils/weight';

export const ShipmentInfoSidePanel = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();

  const origin = getOrigin(shipment);
  const destination = getDestination(shipment);

  return (
    <Stack gap="0.5rem">
      <Accordion
        openByDefault
        trigger={
          <Paragraph weight="medium">
            <Trans id="shipment.information">Information</Trans>
          </Paragraph>
        }
      >
        <Stack ariaLabel={t({ id: 'shipment.information', message: 'Information' })}>
          <ShipmentInfo shipment={shipment} />
        </Stack>
      </Accordion>
      <Separator />
      <Accordion
        openByDefault
        trigger={
          <Paragraph weight="medium">
            <Trans id="shipment.origin.title">Origin</Trans>
          </Paragraph>
        }
      >
        <Stack ariaLabel={t({ id: 'shipment.origin.title', message: 'Origin' })}>
          <ContactDetails
            placeIcon="start"
            addressName={origin?.name}
            address={shipment.originAddress}
            phone={origin && 'contactInfo' in origin ? origin?.contactInfo?.phone : origin?.phone}
            size="small"
          />
        </Stack>
      </Accordion>
      <Separator />
      <Accordion
        openByDefault
        trigger={
          <Paragraph weight="medium">
            <Trans id="shipment.destination.title">Destination</Trans>
          </Paragraph>
        }
      >
        <Stack ariaLabel={t({ id: 'shipment.destination.title', message: 'Destination' })}>
          <ContactDetails
            placeIcon="end"
            addressName={destination?.name}
            address={shipment.destinationAddress}
            phone={
              destination && 'contactInfo' in destination
                ? destination?.contactInfo?.phone
                : destination?.phone
            }
            size="small"
          />
        </Stack>
      </Accordion>
    </Stack>
  );
};

export const ShipmentInfoTopPanel = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();

  const origin = getOrigin(shipment);
  const destination = getDestination(shipment);

  return (
    <Stack gap="1rem">
      <ShipmentOverview shipment={shipment} />
      <Box padding="0">
        <Accordion
          trigger={
            <Stack row gap="0.5rem" alignItems="center">
              <IconFile style={{ fontSize: '1.25rem' }} />
              <Trans id="shipment.information">Information</Trans>
            </Stack>
          }
        >
          <Stack ariaLabel={t({ id: 'shipment.information', message: 'Information' })}>
            <ShipmentInfo shipment={shipment} />
          </Stack>
        </Accordion>
        <Separator />
        <Accordion
          trigger={
            <Stack row gap="0.5rem" alignItems="center">
              <IconPlace start style={{ fontSize: '1.25rem' }} />
              {origin?.name ?? t({ id: 'shipment.origin.title', message: 'Origin' })}
            </Stack>
          }
        >
          <Stack ariaLabel={t({ id: 'shipment.origin.title', message: 'Origin' })}>
            <ContactDetails
              placeIcon="start"
              address={shipment.originAddress}
              phone={origin && 'contactInfo' in origin ? origin?.contactInfo?.phone : origin?.phone}
              size="small"
            />
          </Stack>
        </Accordion>
        <Separator />
        <Accordion
          trigger={
            <Stack row gap="0.5rem" alignItems="center">
              <IconPlace end style={{ fontSize: '1.25rem' }} />
              {destination?.name ?? t({ id: 'shipment.destination.title', message: 'Destination' })}
            </Stack>
          }
        >
          <Stack ariaLabel={t({ id: 'shipment.destination.title', message: 'Destination' })}>
            <ContactDetails
              placeIcon="end"
              address={shipment.destinationAddress}
              phone={
                destination && 'contactInfo' in destination
                  ? destination?.contactInfo?.phone
                  : destination?.phone
              }
              size="small"
            />
          </Stack>
        </Accordion>
      </Box>
    </Stack>
  );
};

const ShipmentInfo = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  return (
    <Stack gap="0.75rem">
      <ShipmentRef shipment={shipment} />
      <CreationDate shipment={shipment} />
      {shipment.statusDueAt && (
        <StatusDueDate date={new Date(shipment.statusDueAt)} variant="info" />
      )}
      <Carrier shipment={shipment} />
      <TrackingNumber shipment={shipment} />
      <NumberOfArticles shipment={shipment} />
      <TotalWeight shipment={shipment} />
      {shipment.pickupDate && <PickupDate shipment={shipment} />}
      {(getArrivalDate(shipment) || shipment.updatedEta || shipment.originalEta) && (
        <DeliveryDate shipment={shipment} />
      )}
    </Stack>
  );
};

const ShipmentRef = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.reference.label', message: 'Prolong ref.' });

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <InlineText weight="medium" aria-label={label}>
        {shipment.reference}
      </InlineText>
    </Stack>
  );
};

const CreationDate = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.creation-date.label', message: 'Creation date' });

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <Stack row alignItems="center" gap="0.25rem" aria-label={label}>
        <IconCalendar style={{ fontSize: '1rem' }} />
        <InlineText weight="medium">
          {formatDate(new Date(shipment.createdAt), { dateStyle: 'medium' })}
        </InlineText>
      </Stack>
    </Stack>
  );
};

const Carrier = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.carrier.label', message: 'Carrier' });
  const carrierName = useCarrierName(shipment);

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <InlineText weight="medium">{carrierName}</InlineText>
    </Stack>
  );
};

const TrackingNumber = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();

  const canEditTrackingNumber =
    shipment.carrier === 'private-carrier' &&
    !['received', 'validated'].includes(shipment.status) &&
    canBeCreatedBy(shipment, currentSession);

  const trackingId = (
    <InlineText weight="medium">
      {shipment.trackingUrl ? (
        <Link
          to={shipment.trackingUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary"
          style={{ fontWeight: 500 }}
        >
          {shipment.trackingId ?? <Trans id="shipment.trackingNumber.unknown">Unknown</Trans>}
        </Link>
      ) : (
        (shipment.trackingId ?? <Trans id="shipment.trackingNumber.unknown">Unknown</Trans>)
      )}
    </InlineText>
  );

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        <Trans id="shipment.trackingNumber.label">Tracking number</Trans>
      </InlineText>
      {canEditTrackingNumber && (
        <TrackingIdModal shipment={shipment}>
          <Label style={{ cursor: 'pointer' }}>
            <Stack row alignItems="center" gap="4px">
              {!shipment.trackingId && (
                <>
                  <Button
                    variant="secondary"
                    size="small"
                    iconOnly
                    ariaLabel={t({
                      id: 'shipment.trackingNumber.add',
                      message: 'Add tracking number',
                    })}
                  >
                    <IconAdd />
                  </Button>
                  <InlineText color="disabled">
                    <Trans id="shipment.trackingNumber.unknown">Unknown</Trans>
                  </InlineText>
                </>
              )}
              {shipment.trackingId && (
                <>
                  {trackingId}
                  <Button
                    variant="secondary"
                    size="small"
                    iconOnly
                    ariaLabel={t({
                      id: 'shipment.trackingNumber.edit',
                      message: 'Edit tracking number',
                    })}
                  >
                    <IconEdit />
                  </Button>
                </>
              )}
            </Stack>
          </Label>
        </TrackingIdModal>
      )}
      {!canEditTrackingNumber && trackingId}
    </Stack>
  );
};

const NumberOfArticles = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.articles.label', message: 'Items' });

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}{' '}
      </InlineText>
      <InlineText weight="medium" aria-label={label}>
        {shipment.articles.length}
      </InlineText>
    </Stack>
  );
};

const TotalWeight = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.weight.label', message: 'Weight' });

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <InlineText weight="medium" aria-label={label}>
        {formatWeight(sumWeights(shipment.articles.map(({ article }) => article.weight)))}
      </InlineText>
    </Stack>
  );
};

const PickupDate = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.pickup-date.label', message: 'Pickup date' });

  if (!shipment.pickupDate) {
    return null;
  }

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <Stack row alignItems="center" gap="0.25rem" aria-label={label}>
        <IconCalendar style={{ fontSize: '1rem' }} />
        <InlineText weight="medium">
          {formatDate(parseISO(shipment.pickupDate), { dateStyle: 'medium' })}
        </InlineText>
      </Stack>
    </Stack>
  );
};

const DeliveryDate = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.delivery-date.label', message: 'Delivery date' });

  return (
    <Stack>
      <InlineText size="200" color="secondary">
        {label}
      </InlineText>
      <Stack row alignItems="center" gap="0.25rem" aria-label={label}>
        <IconCalendar style={{ fontSize: '1rem' }} />
        <InlineText weight="medium">
          <ArrivalDate shipment={shipment} />
        </InlineText>
      </Stack>
    </Stack>
  );
};

const ArrivalDate = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const arrivalDate = getArrivalDate(shipment);
  const updatedEtaDate = shipment.updatedEta ? new Date(shipment.updatedEta) : null;
  const originalEtaDate = shipment.originalEta ? new Date(shipment.originalEta) : null;

  if (arrivalDate || (shipment.carrier === 'private-carrier' && originalEtaDate)) {
    return formatDate(arrivalDate ?? originalEtaDate!, { dateStyle: 'medium' });
  }

  const eta = updatedEtaDate ?? originalEtaDate;

  if (eta) {
    const isLate = startOfDay(eta) < startOfDay(new Date());

    if (isLate) {
      return (
        <>
          {formatDate(eta, { dateStyle: 'medium' })}{' '}
          <Trans id="shipment.estimated.short">(est.)</Trans>
        </>
      );
    }

    return (
      <>
        {formatDate(eta, { dateStyle: 'medium' })}{' '}
        <Trans id="shipment.estimated.short">(est.)</Trans>
      </>
    );
  }

  return '-';
};
